import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Box,
    alpha,
    Collapse,
    IconButton
} from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import {iconMap} from "./Icons";
import {Checkbox} from "./Checkbox";

// Types
export type Order = 'asc' | 'desc';

export interface Column<T> {
    key?: keyof T | string;
    label: string;
    icon?: string;
    align?: 'left' | 'center' | 'right' | 'inherit' | 'justify';
    width?: number | string;
    sortable?: boolean;
    render?: (value: any, row: T, index: number) => React.ReactNode;
    children?: Column<T>[];
}

export interface BaseTableProps<T> {
    data: T[];
    columns: Column<T>[];
    orderBy?: string;
    order?: Order;
    onSort?: (orderBy: string, order: Order) => void;
    rowKey: (row: T) => string | number;

    // Optional features
    showIndex?: boolean;
    showSelection?: boolean;
    showPagination?: boolean;
    expandableRows?: boolean;

    // Selection
    selectedItems?: Set<string | number>;
    onSelectionChange?: (selectedItems: Set<string | number>) => void;

    // Pagination
    page?: number;
    rowsPerPage?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    rowsPerPageOptions?: number[];

    // Styling
    onRowHover?: boolean;
    customRowProps?: (row: T, index: number) => object;

    // Expandable rows
    renderExpandedRow?: (row: T, index: number) => React.ReactNode;
    isRowExpanded?: (row: T) => boolean;
    onRowExpand?: (row: T) => void;
}

// Utility functions
const parseDecimal = (value: string): number => {
    const parsed = parseFloat(value.replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
};

const parseCurrency = (value: string): number => {
    const cleaned = value.replace(/[€$,\s]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
};

const createSortFunction = <T,>(data: T[], orderBy: string, order: Order): T[] => {
    return [...data].sort((a, b) => {
        let aValue: any = getNestedValue(a, orderBy);
        let bValue: any = getNestedValue(b, orderBy);

        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            if (aValue.match(/[€$]/) || bValue.match(/[€$]/)) {
                aValue = parseCurrency(aValue);
                bValue = parseCurrency(bValue);
            } else if (aValue.match(/^\d+([.,]\d+)?$/) && bValue.match(/^\d+([.,]\d+)?$/)) {
                aValue = parseDecimal(aValue);
                bValue = parseDecimal(bValue);
            } else {
                const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                return order === 'asc' ? comparison : -comparison;
            }
        }

        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
    });
};

// Column utilities for grouped columns
const flattenColumns = <T,>(columns: Column<T>[]): Column<T>[] => {
    const flattened: Column<T>[] = [];

    const flatten = (cols: Column<T>[]) => {
        cols.forEach(col => {
            if (col.children && col.children.length > 0) {
                flatten(col.children);
            } else {
                flattened.push(col);
            }
        });
    };

    flatten(columns);
    return flattened;
};

const getMaxDepth = <T,>(columns: Column<T>[]): number => {
    let maxDepth = 1;

    const getDepth = (cols: Column<T>[], currentDepth: number): number => {
        let depth = currentDepth;
        cols.forEach(col => {
            if (col.children && col.children.length > 0) {
                depth = Math.max(depth, getDepth(col.children, currentDepth + 1));
            }
        });
        return depth;
    };

    return getDepth(columns, 1);
};

interface HeaderCell<T> {
    column: Column<T>;
    colSpan: number;
    rowSpan: number;
}

const buildHeaderStructure = <T,>(columns: Column<T>[], maxDepth: number): HeaderCell<T>[][] => {
    const rows: HeaderCell<T>[][] = Array(maxDepth).fill(null).map(() => []);

    const buildRow = (cols: Column<T>[], rowIndex: number, leafColumns: Column<T>[]) => {
        cols.forEach(col => {
            if (col.children && col.children.length > 0) {
                // Group header
                const leafCount = flattenColumns([col]).length;
                rows[rowIndex].push({
                    column: col,
                    colSpan: leafCount,
                    rowSpan: 1
                });
                buildRow(col.children, rowIndex + 1, leafColumns);
            } else {
                // Leaf column
                rows[rowIndex].push({
                    column: col,
                    colSpan: 1,
                    rowSpan: maxDepth - rowIndex
                });
            }
        });
    };

    buildRow(columns, 0, flattenColumns(columns));
    return rows;
};

export const BaseTable = <T,>({
                                  data,
                                  columns,
                                  orderBy: externalOrderBy,
                                  order: externalOrder,
                                  onSort: externalOnSort,
                                  rowKey,
                                  showIndex = false,
                                  showSelection = false,
                                  showPagination = false,
                                  expandableRows = false,
                                  selectedItems,
                                  onSelectionChange,
                                  page = 0,
                                  rowsPerPage = 10,
                                  totalCount,
                                  onPageChange,
                                  onRowsPerPageChange,
                                  rowsPerPageOptions = [5, 10, 25, 50],
                                  onRowHover = true,
                                  customRowProps,
                                  renderExpandedRow,
                                  isRowExpanded,
                                  onRowExpand
                              }: BaseTableProps<T>) => {

    // Internal state for sorting when not controlled externally
    const [internalOrderBy, setInternalOrderBy] = useState<string>('');
    const [internalOrder, setInternalOrder] = useState<Order>('asc');

    // Internal state for expanded rows
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

    // Use external or internal sorting state
    const currentOrderBy = externalOrderBy !== undefined ? externalOrderBy : internalOrderBy;
    const currentOrder = externalOrder !== undefined ? externalOrder : internalOrder;

    // Process columns for grouped structure
    const leafColumns = useMemo(() => flattenColumns(columns), [columns]);
    const maxDepth = useMemo(() => getMaxDepth(columns), [columns]);
    const headerStructure = useMemo(() => buildHeaderStructure(columns, maxDepth), [columns, maxDepth]);

    const handleSort = (columnKey: string) => (event: React.MouseEvent<unknown>) => {
        const isAsc = currentOrderBy === columnKey && currentOrder === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';

        if (externalOnSort) {
            externalOnSort(columnKey, newOrder);
        } else {
            setInternalOrderBy(columnKey);
            setInternalOrder(newOrder);
        }
    };

    // Sort data
    const sortedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        if (!currentOrderBy) return data;
        return createSortFunction(data, currentOrderBy, currentOrder);
    }, [data, currentOrderBy, currentOrder]);

    // Handle selection
    const handleSelectAll = () => {
        if (!onSelectionChange) return;

        const allSelected = sortedData.every(row => selectedItems?.has(rowKey(row)));
        const newSelection = new Set(selectedItems);

        if (allSelected) {
            sortedData.forEach(row => newSelection.delete(rowKey(row)));
        } else {
            sortedData.forEach(row => newSelection.add(rowKey(row)));
        }

        onSelectionChange(newSelection);
    };

    const handleSelectRow = (row: T) => {
        if (!onSelectionChange) return;

        const key = rowKey(row);
        const newSelection = new Set(selectedItems);

        if (newSelection.has(key)) {
            newSelection.delete(key);
        } else {
            newSelection.add(key);
        }

        onSelectionChange(newSelection);
    };

    // Handle row expansion
    const handleRowExpand = (row: T) => {
        const key = rowKey(row);

        if (onRowExpand) {
            onRowExpand(row);
        } else {
            const newExpanded = new Set(expandedRows);
            if (newExpanded.has(key)) {
                newExpanded.delete(key);
            } else {
                newExpanded.add(key);
            }
            setExpandedRows(newExpanded);
        }
    };

    const isExpanded = (row: T) => {
        if (isRowExpanded) {
            return isRowExpanded(row);
        }
        return expandedRows.has(rowKey(row));
    };

    const isSelected = (row: T) => {
        return selectedItems?.has(rowKey(row)) || false;
    };

    const isAllSelected = () => {
        return sortedData.length > 0 && sortedData.every(row => selectedItems?.has(rowKey(row)));
    };

    const isIndeterminate = () => {
        const selectedCount = sortedData.filter(row => selectedItems?.has(rowKey(row))).length;
        return selectedCount > 0 && selectedCount < sortedData.length;
    };

    return (
        <Box>
            <TableContainer component={Box}>
                <Table>
                    <TableHead>
                        {headerStructure.map((headerRow, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {/* Selection and Index columns only in first row */}
                                {rowIndex === 0 && showSelection && (
                                    <TableCell
                                        align="center"
                                        sx={{ width: 50 }}
                                        rowSpan={maxDepth}
                                    >
                                        <Checkbox
                                            id="selectAll"
                                            name="selectAll"
                                            className={'ml-2'}
                                            isSelected={isAllSelected()}
                                            isIndeterminate={isIndeterminate()}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                )}
                                {rowIndex === 0 && showIndex && (
                                    <TableCell
                                        align="center"
                                        sx={{ width: 50 }}
                                        rowSpan={maxDepth}
                                    >
                                        #
                                    </TableCell>
                                )}

                                {headerRow.map((headerCell, cellIndex) => (
                                    <TableCell
                                        key={`${rowIndex}-${cellIndex}`}
                                        align={headerCell.column.align || 'left'}
                                        colSpan={headerCell.colSpan}
                                        rowSpan={headerCell.rowSpan}
                                        sx={{
                                            width: headerCell.column.width,
                                            textAlign: headerCell.column.align === 'right' ? 'right' : headerCell.column.align || 'left',
                                            borderBottom: rowIndex === maxDepth - 1 ? undefined : '1px solid rgba(224, 224, 224, 1)'
                                        }}
                                    >
                                        {headerCell.column.key && headerCell.column.sortable !== false ? (
                                            <TableSortLabel
                                                active={currentOrderBy === headerCell.column.key}
                                                direction={currentOrderBy === headerCell.column.key ? currentOrder : 'asc'}
                                                onClick={handleSort(String(headerCell.column.key))}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {headerCell.column.icon && iconMap[headerCell.column.icon]}
                                                    {headerCell.column.label}
                                                </Box>
                                            </TableSortLabel>
                                        ) : (
                                            headerCell.column.label
                                        )}
                                    </TableCell>
                                ))}

                                {/* Expandable column only in first row */}
                                {rowIndex === 0 && expandableRows && (
                                    <TableCell
                                        align="center"
                                        sx={{ width: 50 }}
                                        rowSpan={maxDepth}
                                    />
                                )}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {sortedData.map((row, index) => {
                            const key = rowKey(row);
                            const expanded = isExpanded(row);
                            const selected = isSelected(row);
                            const customProps = customRowProps ? customRowProps(row, index) : {};

                            return (
                                <React.Fragment key={key}>
                                    <TableRow
                                        hover={onRowHover}
                                        selected={selected}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            ...(onRowHover && {
                                                '&:hover': {
                                                    backgroundColor: (theme) => alpha(theme.palette.common.black, 0.03)
                                                }
                                            }),
                                            ...(selected && {
                                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                            })
                                        }}
                                        {...customProps}
                                    >
                                        {showSelection && (
                                            <TableCell align="center">
                                                <Checkbox
                                                    id="selectAll"
                                                    name="selectAll"
                                                    className={'ml-2'}
                                                    isSelected={selected}
                                                    onChange={() => handleSelectRow(row)}
                                                />
                                            </TableCell>
                                        )}
                                        {showIndex && (
                                            <TableCell align="center" sx={{ width: 50 }}>
                                                {index + 1}
                                            </TableCell>
                                        )}
                                        {leafColumns.map((column) => {
                                            const value = getNestedValue(row, String(column.key));
                                            return (
                                                <TableCell
                                                    key={String(column.key)}
                                                    align={column.align || 'left'}
                                                    sx={{
                                                        width: column.width,
                                                        textAlign: column.align === 'right' ? 'right' : column.align || 'left'
                                                    }}
                                                >
                                                    {column.render ? column.render(value, row, index) : value}
                                                </TableCell>
                                            );
                                        })}
                                        {expandableRows && (
                                            <TableCell align="center">
                                                {renderExpandedRow && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRowExpand(row)}
                                                    >
                                                        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>

                                    {expandableRows && renderExpandedRow && expanded && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={leafColumns.length + (showSelection ? 1 : 0) + (showIndex ? 1 : 0) + 1}
                                                sx={{ py: 0, border: 'none' }}
                                            >
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 2 }}>
                                                        {renderExpandedRow(row, index)}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {showPagination && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={totalCount || sortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelRowsPerPage="Anzahl der Zeilen"
                    onPageChange={(_, newPage) => onPageChange?.(newPage)}
                    onRowsPerPageChange={(event) =>
                        onRowsPerPageChange?.(parseInt(event.target.value, 10))
                    }
                />
            )}
        </Box>
    );
};

// Hook for managing table state
export const useEnhancedTable = (
    initialOrderBy: string = '',
    initialOrder: Order = 'asc'
) => {
    const [orderBy, setOrderBy] = useState<string>(initialOrderBy);
    const [order, setOrder] = useState<Order>(initialOrder);
    const [selectedItems, setSelectedItems] = useState<Set<string | number>>(new Set());
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleSort = (newOrderBy: string, newOrder: Order) => {
        setOrderBy(newOrderBy);
        setOrder(newOrder);
    };

    const handleSelectionChange = (newSelection: Set<string | number>) => {
        setSelectedItems(newSelection);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const clearSelection = () => {
        setSelectedItems(new Set());
    };

    return {
        orderBy,
        order,
        selectedItems,
        page,
        rowsPerPage,
        handleSort,
        handleSelectionChange,
        handlePageChange,
        handleRowsPerPageChange,
        clearSelection,
        setOrderBy,
        setOrder,
        setPage,
        setRowsPerPage
    };
};